import React, { useRef } from "react";
import { FormInput } from "@components/styles";
import { LoadScript, Autocomplete, Libraries } from "@react-google-maps/api";
import { AddressComponents } from "@lib/utils";

const libraries: Libraries = ["places"];
const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_API_KEY;

type AddressAutocompleteProps = {
  onCoordinates: (data: AddressComponents) => void;
};

const getComponent = (components: google.maps.GeocoderAddressComponent[], type: string) =>
  components.find((c) => c.types.includes(type))?.long_name || "";

const AddressAutocomplete = ({ onCoordinates }: AddressAutocompleteProps) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocomplete.setOptions({
      componentRestrictions: { country: "us" }
    });
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place.geometry && place.geometry.location && place.address_components) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const components = place.address_components;

        const data: AddressComponents = {
          address: place.formatted_address || "",
          houseNumber: getComponent(components, "street_number"),
          streetName: getComponent(components, "route"),
          city: getComponent(components, "locality") || getComponent(components, "sublocality"),
          postalCode: getComponent(components, "postal_code"),
          latitude: lat,
          longitude: lng
        };

        // Optional: Get elevation from Google ElevationService
        const elevator = new google.maps.ElevationService();
        elevator.getElevationForLocations({ locations: [place.geometry.location] }, (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            data.elevation = results[0].elevation;
          }
          onCoordinates(data);
        });
      } else {
        console.error("Missing geometry or address components.");
      }
    }
  };

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <FormInput
          type="text"
          placeholder="Enter an address"
          style={{
            boxSizing: "border-box",
            padding: 6,
            width: "100%",
            maxWidth: 600,
            borderRadius: 4,
            border: "1px solid hsl(0, 0%, 80%)",
            minHeight: 38
          }}
        />
      </Autocomplete>
    </LoadScript>
  );
};

export { AddressAutocomplete };
